import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { payslipApi, type SavePayslipPayload } from "../lib/payslipApi";

export const PAYSLIP_KEYS = {
  all: ["payslips"] as const,
  mine: () => [...PAYSLIP_KEYS.all, "mine"] as const,
  mineDetail: (businessId: string, period: string) =>
    [...PAYSLIP_KEYS.all, "mine", businessId, period] as const,
  adminList: (period: string) => [...PAYSLIP_KEYS.all, "admin", period] as const,
  adminDetail: (userId: string, period: string) =>
    [...PAYSLIP_KEYS.all, "admin", userId, period] as const,
  bizAdminList: (businessId: string, period: string) =>
    [...PAYSLIP_KEYS.all, "bizAdmin", businessId, period] as const,
  bizAdminDetail: (businessId: string, userId: string, period: string) =>
    [...PAYSLIP_KEYS.all, "bizAdmin", businessId, userId, period] as const,
};

const QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
} as const;

export function useMyPayslips() {
  return useInfiniteQuery({
    queryKey: PAYSLIP_KEYS.mine(),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      payslipApi.listMine({ limit: 10, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    ...QUERY_OPTIONS,
  });
}

export function useMyPayslip(businessId: string, period: string) {
  return useQuery({
    queryKey: PAYSLIP_KEYS.mineDetail(businessId, period),
    queryFn: () => payslipApi.getMine(businessId, period).then((r) => r.payslip),
    enabled: !!businessId && !!period,
    ...QUERY_OPTIONS,
  });
}

// ── Super-admin hooks ─────────────────────────────────────────────────────────

export function useAdminPayslips(period: string, options?: { enabled?: boolean; businessId?: string }) {
  return useQuery({
    queryKey: PAYSLIP_KEYS.adminList(period),
    queryFn: () => payslipApi.adminList(period, 50, undefined, options?.businessId),
    enabled: (options?.enabled ?? true) && !!period,
    ...QUERY_OPTIONS,
  });
}

export function useAdminPayslipDetail(userId: string, period: string, businessId?: string) {
  return useQuery({
    queryKey: PAYSLIP_KEYS.adminDetail(userId, period),
    queryFn: () => payslipApi.adminGet(userId, period, businessId),
    enabled: !!userId && !!period,
    ...QUERY_OPTIONS,
  });
}

export function useSavePayslip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SavePayslipPayload) => payslipApi.adminSave(payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: PAYSLIP_KEYS.all });
    },
  });
}

export function useFinalizePayslip(businessId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, period }: { userId: string; period: string }) =>
      payslipApi.adminFinalize(userId, period, businessId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: PAYSLIP_KEYS.all });
    },
  });
}

// ── Business-scoped admin hooks ───────────────────────────────────────────────

export function useBizAdminPayslips(businessId: string, period: string) {
  return useQuery({
    queryKey: PAYSLIP_KEYS.bizAdminList(businessId, period),
    queryFn: () => payslipApi.bizAdminList(businessId, period),
    enabled: !!businessId && !!period,
    ...QUERY_OPTIONS,
  });
}

export function useBizAdminPayslipDetail(
  businessId: string,
  userId: string,
  period: string,
) {
  return useQuery({
    queryKey: PAYSLIP_KEYS.bizAdminDetail(businessId, userId, period),
    queryFn: () => payslipApi.bizAdminGet(businessId, userId, period),
    enabled: !!businessId && !!userId && !!period,
    ...QUERY_OPTIONS,
  });
}

export function useSaveBizPayslip(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SavePayslipPayload) =>
      payslipApi.bizAdminSave(businessId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: PAYSLIP_KEYS.all });
    },
  });
}

export function useFinalizeBizPayslip(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, period }: { userId: string; period: string }) =>
      payslipApi.bizAdminFinalize(businessId, userId, period),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: PAYSLIP_KEYS.all });
    },
  });
}
